import { useRuntimeConfig } from '#imports'
import { ZodError } from 'zod'
import type { AvailabilityLocation } from '~/composables/AvailabilityLocation'
import { useApiRequest } from '~/composables/useApiRequest'
import { useIdentity } from '~/composables/useIdentity'
import { useLogger } from '~/composables/useLogger'
import type { ApiRequest } from '~/types/ApiRequest'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import type { OrderType } from '~/enums'
import { useApiClient } from '~/services/api'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { FavoritesIndex } from '~/services/api/contracts/requests/FavoritesIndex'
import {
  FavoritesIndexSuccessSchema,
  StoreAvailabilityCheckQuerySchema,
  StoreAvailabilityCheckSuccessSchema,
  type StoreAvailabilityCheckSuccess
} from '~/services/api/generated'
import { resolveProblemMessage } from '~/services/api/resolveProblemMessage'
import { useCartStore } from '~/stores/useCartStore'
import { useFiltersStore } from '~/stores/useFiltersStore'
import { useSearchStore } from '~/stores/useSearchStore'
import { identityGeneration } from '~/utils/identityGeneration'
import { isGroupDining } from '~/utils/isGroupDining'
import { Normalization } from '~/utils/normalization'
import { resolveEventDateOnly } from '~/utils/resolveEventDateOnly'

interface SearchApplication {
  filtersStore: ReturnType<typeof useFiltersStore>
  searchStore: ReturnType<typeof useSearchStore>
  fetchResults: (
    filters?: Parameters<ReturnType<typeof useSearchStore>['buildQueryParams']>[0]
  ) => Promise<void>
  fetchEventRestaurants: (
    slug: string,
    filters?: Parameters<ReturnType<typeof useSearchStore>['buildQueryParams']>[0]
  ) => Promise<void>
  fetchFavoriteStores: (
    filters?: Parameters<ReturnType<typeof useSearchStore>['buildFavoritesQueryParams']>[0]
  ) => Promise<void>
  fetchAvailability: (
    storeSlug: string,
    options?: {
      subtotal?: number | string | null
      checkMenu?: boolean
      timezone?: string | null
      eventDate?: string
      eventTime?: string
      orderType?: OrderType
      location?: AvailabilityLocation
      guestCount?: number
    }
  ) => Promise<Exclude<StoreAvailabilityCheckSuccess, string>>
}

const groupDiningAvailabilityQuerySchema = StoreAvailabilityCheckQuerySchema.extend({
  guest_count: StoreAvailabilityCheckQuerySchema.shape.guest_count.unwrap().unwrap()
})
const partylessAvailabilityQuerySchema = StoreAvailabilityCheckQuerySchema.omit({
  guest_count: true
})

const searchRadiusMiles = (): number => Number(useRuntimeConfig().public.searchRadiusMiles) || 25

const normalizeAvailabilitySubtotal = (value: unknown): number | null => {
  const parsed = Number.parseFloat(String(value ?? ''))
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : null
}

const resolveAvailabilitySubtotal = (subtotalOverride: unknown = null): number | null => {
  const normalizedOverride = normalizeAvailabilitySubtotal(subtotalOverride)
  if (normalizedOverride !== null) return normalizedOverride

  const cartStore = useCartStore()
  return normalizeAvailabilitySubtotal(cartStore.currentStoreData?.subtotal)
}

function buildSearchResultsQuery(
  searchStore: SearchApplication['searchStore'],
  filters: NonNullable<Parameters<SearchApplication['fetchResults']>[0]>,
  coords: SearchApplication['searchStore']['coordinates'],
  addressString: string,
  identity: ReturnType<typeof useIdentity>['identity']
): Record<string, unknown> {
  const query = { ...searchStore.buildQueryParams(filters) }
  if (addressString) query['q'] = addressString

  const hasLatitude = coords !== null && Boolean(coords.lat) && !Number.isNaN(coords.lat)
  const hasLongitude = coords !== null && Boolean(coords.lng) && !Number.isNaN(coords.lng)
  if (hasLatitude) query['lat'] = Number(coords.lat)
  if (hasLongitude) query['lng'] = Number(coords.lng)
  searchStore.applyBoundsAndPlaceTypeParams(query)

  const hasRadius = Boolean(query['radius_miles'])
  if (!hasRadius) {
    const hasRadiusFilter = Boolean(filters.radius_miles) && !Number.isNaN(filters.radius_miles)
    query['radius_miles'] = hasRadiusFilter
      ? Normalization.floatValue(filters.radius_miles, Number.NaN)
      : searchRadiusMiles()
  }

  const currentUser = identity.value
  const hasUser = currentUser !== null
  if (hasUser) query['userId'] = currentUser.id
  delete query['event_slug']
  return query
}

function resolveAvailabilitySelection(
  searchStore: SearchApplication['searchStore'],
  options: NonNullable<Parameters<SearchApplication['fetchAvailability']>[1]>
) {
  const { subtotal: subtotalOverride = null, checkMenu = false } = options
  const location = options.location
  const omitLocation = location?.kind === 'omitted'
  const addressLocation = location?.kind === 'address' ? location : undefined
  const currentOrderType = options.orderType ?? searchStore.orderType
  const currentEventDateTime = searchStore.eventDateTime
  const currentEventDetails = searchStore.eventDetails
  const requestedCoordinates =
    addressLocation === undefined ? searchStore.coordinates : (addressLocation.coordinates ?? null)
  const currentCoordinates = omitLocation ? null : requestedCoordinates
  const currentAddress = omitLocation ? null : (addressLocation?.address ?? searchStore.searchQuery)
  const selectedEventDate =
    options.eventDate ??
    searchStore.resolveEventDateForQuery(currentEventDateTime?.eventDate, false, options.timezone)
  const selectedEventTime = options.eventTime ?? currentEventDateTime?.eventTime
  const hasEventDate = Boolean(selectedEventDate)
  const hasEventTime = Boolean(selectedEventTime)
  if (!hasEventDate || !hasEventTime) {
    throw new Error('A date and time are required to check availability.')
  }
  return {
    subtotalOverride,
    checkMenu,
    omitLocation,
    currentOrderType,
    currentEventDetails,
    currentCoordinates,
    currentAddress,
    selectedEventDate,
    selectedEventTime
  }
}

function admitAvailabilityQuery(
  storeSlug: string,
  selection: ReturnType<typeof resolveAvailabilitySelection>,
  options: NonNullable<Parameters<SearchApplication['fetchAvailability']>[1]>
) {
  const {
    subtotalOverride,
    checkMenu,
    omitLocation,
    currentOrderType,
    currentEventDetails,
    currentCoordinates,
    currentAddress,
    selectedEventDate,
    selectedEventTime
  } = selection
  let addressString = ''
  if (!omitLocation) {
    if (typeof currentAddress === 'string') {
      addressString = currentAddress
    } else {
      const queryAddress = currentAddress?.q
      const hasQueryAddress = Boolean(queryAddress)
      if (hasQueryAddress) {
        addressString = queryAddress ?? ''
      } else {
        const address = currentAddress?.address
        addressString = address ?? ''
      }
    }
  }

  const queryParams: Record<string, unknown> = {
    store_id: storeSlug,
    order_type: currentOrderType,
    address: addressString
  }
  const subtotal = resolveAvailabilitySubtotal(subtotalOverride)
  if (subtotal !== null && subtotal > 0) queryParams['subtotal'] = subtotal

  const hasLatitude =
    currentCoordinates !== null &&
    Boolean(currentCoordinates.lat) &&
    !Number.isNaN(currentCoordinates.lat)
  const hasLongitude =
    currentCoordinates !== null &&
    Boolean(currentCoordinates.lng) &&
    !Number.isNaN(currentCoordinates.lng)
  if (!omitLocation && hasLatitude && hasLongitude) {
    queryParams['lat'] = Number(currentCoordinates.lat)
    queryParams['lng'] = Number(currentCoordinates.lng)
  }

  queryParams['requested_date'] =
    options.eventDate ?? resolveEventDateOnly(selectedEventDate, options.timezone)
  queryParams['requested_time'] = selectedEventTime
  const isGroupDiningRequest = isGroupDining(currentOrderType)
  if (isGroupDiningRequest) {
    queryParams['guest_count'] = options.guestCount ?? currentEventDetails?.guestCount ?? undefined
  }
  if (checkMenu) queryParams['check_menu'] = true

  const parsedQuery = (
    isGroupDiningRequest ? groupDiningAvailabilityQuerySchema : partylessAvailabilityQuerySchema
  ).safeParse(queryParams)
  if (!parsedQuery.success) {
    throw AppProblemModel.fromZodError(parsedQuery.error, { context: 'availability' })
  }
  return parsedQuery.data
}

class SearchApplicationOperations implements SearchApplication {
  readonly searchStore: SearchApplication['searchStore']
  readonly filtersStore: SearchApplication['filtersStore']
  readonly #api: ReturnType<typeof useApiClient>
  readonly #sanctumClient: ApiRequest
  readonly #identity: ReturnType<typeof useIdentity>['identity']
  readonly #t: ReturnType<typeof usePresentationTranslator>
  readonly #logger: ReturnType<typeof useLogger>

  constructor(
    searchStore: SearchApplication['searchStore'],
    filtersStore: SearchApplication['filtersStore'],
    api: ReturnType<typeof useApiClient>,
    sanctumClient: ApiRequest,
    identity: ReturnType<typeof useIdentity>['identity'],
    t: ReturnType<typeof usePresentationTranslator>,
    logger: ReturnType<typeof useLogger>
  ) {
    this.searchStore = searchStore
    this.filtersStore = filtersStore
    this.#api = api
    this.#sanctumClient = sanctumClient
    this.#identity = identity
    this.#t = t
    this.#logger = logger
  }

  fetchResults: SearchApplication['fetchResults'] = async (filters = {}) => {
    const searchStore = this.searchStore
    const generation = identityGeneration.current()
    const hasEventSlug = Boolean(searchStore.eventSlug)
    const hasCoordinates = searchStore.coordinates !== null
    const hasSearchQuery = Boolean(searchStore.searchQuery)
    if (!hasEventSlug && hasCoordinates && !hasSearchQuery) searchStore.coordinates = null

    const coords = searchStore.coordinates
    const addressString = searchStore.getAddressString()
    searchStore.loading = true
    searchStore.clearSearchError()
    searchStore.pagination = { page: null, per_page: null, total: null, last_page: null }
    searchStore.hasInitialized = true

    try {
      const query = buildSearchResultsQuery(
        searchStore,
        filters,
        coords,
        addressString,
        this.#identity
      )

      const response = await this.#api.search().search(query)
      if (!identityGeneration.isCurrent(generation)) return
      searchStore.handleApiResponse(response)
      searchStore.clearSearchError()
    } catch (error: unknown) {
      if (!identityGeneration.isCurrent(generation)) return
      if (error instanceof ZodError) this.#logger.error('fetchResults', { issues: error.issues })
      const problem = AppProblemModel.normalize(error, { context: 'store search' })
      searchStore.setSearchError(
        () => `Failed to load search results: ${resolveProblemMessage(problem, this.#t)}`
      )
      searchStore.restaurants = []
    } finally {
      if (identityGeneration.isCurrent(generation)) searchStore.loading = false
    }
  }

  fetchFavoriteStores: SearchApplication['fetchFavoriteStores'] = async (filters = {}) => {
    const searchStore = this.searchStore
    const generation = identityGeneration.current()
    searchStore.favoritesLoading = true
    searchStore.clearFavoritesError()
    searchStore.favoritesHasInitialized = true

    try {
      const query = FavoritesIndex({}).parse(searchStore.buildFavoritesQueryParams(filters))
      const response: unknown = await this.#sanctumClient('/api/dashboard/favorites/stores', {
        method: 'GET',
        query
      })
      const parsedResponse = FavoritesIndexSuccessSchema.parse(response)
      if (typeof parsedResponse === 'string') throw new Error('Invalid favorites response')
      if (!identityGeneration.isCurrent(generation)) return
      searchStore.handleFavoritesApiResponse(parsedResponse)
      searchStore.clearFavoritesError()
    } catch (error: unknown) {
      if (!identityGeneration.isCurrent(generation)) return
      const problem = AppProblemModel.normalize(error, { context: 'store search' })
      searchStore.setFavoritesError(
        () => `Failed to load favorite stores: ${resolveProblemMessage(problem, this.#t)}`
      )
      searchStore.favoriteStores = []
    } finally {
      if (identityGeneration.isCurrent(generation)) searchStore.favoritesLoading = false
    }
  }

  fetchEventRestaurants: SearchApplication['fetchEventRestaurants'] = async (
    slug,
    filters = {}
  ) => {
    const searchStore = this.searchStore
    const generation = identityGeneration.current()
    searchStore.eventSlug = slug
    searchStore.loading = true
    searchStore.clearSearchError()
    searchStore.pagination = { page: null, per_page: null, total: null, last_page: null }
    searchStore.restaurants = []
    searchStore.hasInitialized = true

    try {
      const query = searchStore.buildQueryParams(filters)
      const addressString = searchStore.getAddressString()
      const coords = searchStore.coordinates
      if (addressString) query['q'] = addressString
      const hasLatitude = coords !== null && Boolean(coords.lat) && !Number.isNaN(coords.lat)
      const hasLongitude = coords !== null && Boolean(coords.lng) && !Number.isNaN(coords.lng)
      if (hasLatitude && hasLongitude) {
        query['lat'] = Number(coords.lat)
        query['lng'] = Number(coords.lng)
      }
      searchStore.applyBoundsAndPlaceTypeParams(query)

      const response = await this.#api.search().search(query)
      if (!identityGeneration.isCurrent(generation)) return
      searchStore.handleApiResponse(response)
    } catch (error: unknown) {
      if (!identityGeneration.isCurrent(generation)) return
      const problem = AppProblemModel.normalize(error, { context: 'store search' })
      searchStore.setSearchError(
        () => `Failed to fetch restaurants: ${resolveProblemMessage(problem, this.#t)}`
      )
      searchStore.restaurants = []
    } finally {
      if (identityGeneration.isCurrent(generation)) searchStore.loading = false
    }
  }

  fetchAvailability: SearchApplication['fetchAvailability'] = async (storeSlug, options = {}) => {
    if (!storeSlug) throw new Error('Store slug is required')

    const selection = resolveAvailabilitySelection(this.searchStore, options)

    const baseUrl = useRuntimeConfig().public.apiBaseUrl
    if (!baseUrl) throw new Error('NUXT_PUBLIC_API_BASE_URL is not set — see .env.example')

    const query = admitAvailabilityQuery(storeSlug, selection, options)

    const response: unknown = await $fetch(`${baseUrl}/api/stores/availability`, {
      method: 'GET',
      query
    })
    return StoreAvailabilityCheckSuccessSchema.parse(response)
  }
}

export function useSearchApplication(): SearchApplication {
  const searchStore = useSearchStore()
  const filtersStore = useFiltersStore()
  const api = useApiClient()
  const sanctumClient = useApiRequest()
  const { identity } = useIdentity()
  const t = usePresentationTranslator()
  const logger = useLogger('SearchStore')
  return new SearchApplicationOperations(
    searchStore,
    filtersStore,
    api,
    sanctumClient,
    identity,
    t,
    logger
  )
}
